import {
  isNotificationKindAlwaysOn,
  NOTIFICATION_KIND_GROUPS,
  NOTIFICATION_KIND_LABELS,
  type NotificationAudience,
  type NotificationKindSetting,
  notificationAudienceOf,
} from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { NotificationKindToggle } from '@/features/notification/settings';
import { ContentCard } from '@/shared/ui';

interface KindSettingsSectionProps {
  /** 서버가 내린 종류별 설정. 빠진 종류는 켜짐으로 본다(설정 행이 없으면 켜짐, 알림 설계 §4-3). */
  readonly kinds: readonly NotificationKindSetting[];
}

const DESCRIPTION =
  '끈 알림은 보내지 않고 발송 이력에 "꺼진 알림"으로 남습니다. 바꾼 설정은 다음 발송부터 적용됩니다. 계정·결제 완료·환불 완료 메일은 끌 수 없습니다.';

const AUDIENCE_LABELS: Readonly<Record<NotificationAudience, string>> = {
  CUSTOMER: '고객 메일',
  OPERATOR: '운영자 메일',
};

/**
 * 알림 유형별 발송(알림 설계 N3·§6-2) — 와이어프레임의 화면 묶음(NOTIFICATION_KIND_GROUPS) 순서로 17종을 보인다. 묶음마다
 * 이름 붙은 영역이다. 항상-발송 5종(계정 3종·결제 완료·환불 완료)은 스위치 대신 "항상 발송"이다.
 */
export const KindSettingsSection = ({ kinds }: KindSettingsSectionProps): ReactElement => {
  const headingIdBase = useId();
  const settingOf = new Map(kinds.map((setting) => [setting.kind, setting]));

  return (
    <ContentCard title="알림 유형별 발송" description={DESCRIPTION}>
      <div className="flex flex-col gap-6">
        {NOTIFICATION_KIND_GROUPS.map((group, groupIndex) => {
          const headingId = `${headingIdBase}-${groupIndex}`;

          return (
            <section key={group.label} aria-labelledby={headingId}>
              <h3 id={headingId} className="mb-2 text-sm font-semibold text-muted-foreground">
                {group.label}
              </h3>
              <ul className="divide-y divide-foreground/[0.06] rounded-xl border border-foreground/[0.06]">
                {group.kinds.map((kind) => {
                  const setting = settingOf.get(kind);
                  const alwaysOn = setting?.alwaysOn ?? isNotificationKindAlwaysOn(kind);

                  return (
                    <li
                      key={kind}
                      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{NOTIFICATION_KIND_LABELS[kind]}</p>
                        <p className="text-xs text-muted-foreground">
                          {AUDIENCE_LABELS[notificationAudienceOf(kind)]}
                        </p>
                      </div>
                      {alwaysOn ? (
                        <span className="text-sm text-muted-foreground">항상 발송</span>
                      ) : (
                        <NotificationKindToggle kind={kind} enabled={setting?.enabled ?? true} />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </ContentCard>
  );
};
